/**
 * Plateforme multi evenement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/i18n/fr.etatApplication.ts
 *
 * Rôle :
 *   Les textes du tableau « État de l'application » (cockpit et tableau de
 *   bord), des étapes et confirmations du cockpit, et des badges de test.
 *
 *   Demande de Mika (2026-10-05), après l'ouverture du scan réel cinq jours
 *   avant le jour J : « je dois voir clairement si on a une phase de test de
 *   badge activée ou non. Les différents modes doivent être beaucoup plus
 *   clairs, beaucoup mieux expliqués, il ne peut pas y avoir d'ambiguïté. »
 *
 *   Règle d'écriture : chaque phrase dit l'EFFET réel, pour quelqu'un qui
 *   n'a jamais vu le réglage. Aucun mot d'événement en dur : jetons
 *   d'identité ({eventTypeDef}, {eventDateLong}…) et de lexique
 *   ({lexExhibitorPlural}, {lexWorkshopPlural}…). Apostrophe typographique
 *   partout (une apostrophe droite devant une accolade l'échapperait en ICU).
 *
 * Mikael - chef de projet - 2026-10-05
 */

export const messagesEtatApplicationFr: Record<string, string> = {
  // ── Tableau « État de l'application » ─────────────────────────────────
  'etatApp.titre': 'État de l’application, en ce moment',
  'etatApp.intro':
    'Calculé par le serveur avec les règles qui décident vraiment. Chaque ligne dit ce qui se passe pour de vrai, pas la valeur d’un réglage.',
  'etatApp.calculeLe': 'Calculé le {quand}.',
  'etatApp.indisponible':
    'L’état de l’application n’a pas pu être calculé. Ne changez rien avant de l’avoir revu : rechargez la page.',
  'etatApp.col.question': 'Question',
  'etatApp.col.reponse': 'Réponse',
  'etatApp.col.sens': 'Ce que ça veut dire',
  'etatApp.oui': 'Oui',
  'etatApp.non': 'Non',
  'etatApp.alertes.titre': '{n, plural, one {# alerte rouge} other {# alertes rouges}} : combinaison dangereuse',
  'etatApp.alertes.corriger': 'Pour corriger : {texte}',
  'etatApp.attention.titre': 'À savoir',
  'etatApp.toutVaBien': 'Aucune alerte : les réglages sont cohérents avec la date.',

  // Scan réel
  'etatApp.q.scanReel': 'Le scan réel des badges est-il ouvert ?',
  'etatApp.scanReel.auto_avant':
    'Automatique : il s’ouvrira le {quand}. D’ici là, un vrai badge scanné est refusé et rien n’est enregistré.',
  'etatApp.scanReel.auto_avant.sansDate':
    'Automatique : il s’ouvrira au début {eventTypeOf}, mais la date de début n’est pas configurée.',
  'etatApp.scanReel.auto_pendant':
    'Automatique : {eventTypeDef} a lieu en ce moment. Chaque badge scanné est enregistré pour de bon.',
  'etatApp.scanReel.auto_apres': 'Automatique : la date de fin est passée, les vrais badges sont refusés.',
  'etatApp.scanReel.force_test':
    'Forcé ouvert pour un test. Chaque vrai badge scanné est ENREGISTRÉ pour de bon. Remettez le scan des badges sur « Automatique » dès le test fini.',
  'etatApp.scanReel.etape_forcee_pendant':
    'Ouvert parce que l’étape est forcée sur « Pendant ». Chaque vrai badge scanné est enregistré, quelle que soit la date.',
  'etatApp.scanReel.etape_forcee_avant':
    'Fermé parce que l’étape est forcée sur « Avant ». Les vrais badges sont refusés, même le jour J.',
  'etatApp.scanReel.etape_forcee_apres':
    'Fermé parce que l’étape est forcée sur « Après ». Les vrais badges sont refusés.',

  // Mode entraînement
  'etatApp.q.entrainement':
    'Les {lexExhibitorPlural} et les animateurs peuvent-ils s’entraîner avec des badges de test ?',
  'etatApp.entrainement.ouvert':
    'Mode entraînement ouvert : ils génèrent et scannent des badges de test, rien n’est enregistré. Il se fermera de lui-même au début {eventTypeOf}.',
  'etatApp.entrainement.reglage_desactive':
    'Mode entraînement désactivé : aucun badge de test n’est accepté. Activez-le avec le verrou « Mode entraînement » ci-dessous.',
  'etatApp.entrainement.ferme_pendant':
    'Fermé de lui-même : l’étape est « Pendant ». Un badge de test scanné est refusé avec le message « badge de test, il ne compte pas ».',
  'etatApp.entrainement.ferme_apres': 'Fermé : la date de fin est passée.',

  // Étape
  'etatApp.q.etape': 'Étape {eventTypeOf}',
  'etatApp.etape.before': 'Avant',
  'etatApp.etape.during': 'Pendant',
  'etatApp.etape.after': 'Après',
  'etatApp.etape.mode.auto': 'automatique',
  'etatApp.etape.mode.forcee': 'FORCÉE',
  'etatApp.etape.prochain.during': 'Automatique : passera d’elle-même à « Pendant » le {quand}.',
  'etatApp.etape.prochain.after': 'Automatique : passera d’elle-même à « Après » le {quand}.',
  'etatApp.etape.prochain.aucun': 'Automatique : plus aucun changement, la date de fin est passée.',
  'etatApp.etape.prochain.sansDate': 'Automatique, mais la date de début n’est pas configurée.',
  'etatApp.etape.forcee':
    'Forcée à la main sur « {forcee} » : la date est ignorée. Selon la date, l’étape serait « {auto} ».',

  // Accès
  'etatApp.q.acces': 'Qui peut entrer dans l’application ?',
  'etatApp.acces.open': 'Tout le monde',
  'etatApp.acces.open.sens': 'Site ouvert au public : consultation et inscriptions selon les lignes ci-dessous.',
  'etatApp.acces.exhibitor_only': '{lexExhibitorPluralCap}, animateurs, administrateurs',
  'etatApp.acces.exhibitor_only.sens': 'Le public voit une page d’attente.',
  'etatApp.acces.maintenance': 'Administrateurs seulement',
  'etatApp.acces.maintenance.sens': 'Tout le monde voit une page d’attente.',

  // Inscriptions, édition, connexion, maintenance
  'etatApp.q.inscriptionExposants': 'Les {lexExhibitorPlural} et les animateurs peuvent-ils créer leur compte ?',
  'etatApp.inscriptionExposants.oui': 'Le formulaire d’inscription leur est ouvert.',
  'etatApp.inscriptionExposants.non': 'Le formulaire d’inscription leur est fermé.',
  'etatApp.q.edition': 'Peuvent-ils modifier leur fiche et leurs {lexWorkshopPlural} ?',
  'etatApp.edition.oui': 'Leur espace est ouvert, les modifications sont enregistrées.',
  'etatApp.edition.non': 'Leur espace est fermé (accès réservé ou maintenance).',
  'etatApp.q.profils': 'Quels profils du public peuvent s’inscrire ?',
  'etatApp.profils.aucun': 'Aucun',
  'etatApp.profils.sens': 'Les cartes d’inscription ouvertes sur l’écran de choix du profil.',
  'etatApp.profils.anticipee':
    'Inscription anticipée active : avant l’ouverture au public, seuls ces profils peuvent s’inscrire.',
  'etatApp.q.connexion': 'Faut-il un compte pour consulter le contenu ?',
  'etatApp.connexion.oui':
    'Sans compte, on ne voit que l’accueil, l’inscription, la connexion et les pages d’information.',
  'etatApp.connexion.non': 'Le contenu est consultable sans compte.',
  'etatApp.q.maintenance': 'L’application est-elle en maintenance technique ?',
  'etatApp.maintenance.oui':
    'Tout le monde, {lexExhibitorPlural} compris, voit une page de maintenance et le scan est bloqué. Seuls les administrateurs entrent.',
  'etatApp.maintenance.non': 'L’application fonctionne normalement.',

  // Alertes rouges
  'etatApp.alerte.etape_forcee_pendant_avant_date':
    'L’étape est forcée sur « Pendant » alors que {eventTypeDef} n’a pas commencé.',
  'etatApp.alerte.etape_forcee_pendant_avant_date.corriger':
    'remettez l’étape sur « Automatique » (bloc « Forcer l’étape »).',
  'etatApp.alerte.scan_reel_ouvert_avant_jour_j':
    'Le scan réel des badges est ouvert avant le jour J : chaque badge scanné est enregistré pour de bon.',
  'etatApp.alerte.scan_reel_ouvert_avant_jour_j.corriger':
    'remettez l’étape et le scan des badges sur « Automatique », sauf test en cours.',
  'etatApp.alerte.scan_reel_ferme_pendant_evenement':
    'Le jour J est arrivé mais le scan réel est FERMÉ : les vrais badges sont refusés.',
  'etatApp.alerte.scan_reel_ferme_pendant_evenement.corriger':
    'remettez l’étape sur « Automatique ».',
  'etatApp.alerte.entrainement_actif_pendant_evenement':
    'Le mode entraînement est resté activé alors que {eventTypeDef} a lieu.',
  'etatApp.alerte.entrainement_actif_pendant_evenement.corriger':
    'décochez « Mode entraînement ». Si l’étape est forcée sur « Avant », remettez-la aussi sur « Automatique ».',
  'etatApp.alerte.maintenance_pendant_evenement':
    '{eventTypeDefCap} a commencé et l’application est en maintenance : personne ne peut l’utiliser, le scan est bloqué.',
  'etatApp.alerte.maintenance_pendant_evenement.corriger':
    'choisissez l’étape « Ouvert au public », ou cochez « Application en service ».',

  // Points d'attention
  'etatApp.attention.etape_forcee':
    'L’étape est forcée sur « {forcee} » : la date est ignorée tant qu’elle n’est pas remise sur « Automatique ».',
  'etatApp.attention.entrainement_desactive_avant':
    'Le mode entraînement est désactivé : les {lexExhibitorPlural} et les animateurs ne peuvent pas s’entraîner avec des badges de test avant le jour J.',
  'etatApp.attention.date_non_configuree':
    'La date de début n’est pas configurée : l’étape automatique ne peut pas changer à l’heure.',

  // Résumé du tableau de bord
  'etatApp.resume.titre': 'État de l’application',
  'etatApp.resume.lien': 'Voir le détail et piloter',

  // ── Cockpit : étapes, effets sur le scan, confirmations ──────────────
  'pilotage.stage.jour_j.title': 'Jour J forcé',
  'pilotage.stage.jour_j.desc':
    'Comme « Ouvert au public », et l’étape est FORCÉE sur « Pendant », quelle que soit la date.',
  'pilotage.stage.preparation.scan': 'Scan réel : automatique (jour J seulement).',
  'pilotage.stage.exhibitors_open.scan': 'Scan réel : automatique (jour J seulement).',
  'pilotage.stage.public_open.scan': 'Scan réel : n’est PAS ouvert, il s’ouvrira automatiquement le jour J.',
  'pilotage.stage.jour_j.scan': 'Scan réel : OUVERT IMMÉDIATEMENT, même avant la date.',
  'pilotage.stage.after.scan': 'Scan réel : automatique, fermé après l’heure de fin.',
  'pilotage.confirm.stageAuto':
    'Appliquer l’étape « {stage} » ?\n\n{desc}\n\nL’étape {eventTypeOf} repasse sur « Automatique » : le scan réel ne s’ouvrira qu’au début {eventTypeOf}.',
  'pilotage.confirm.jourJ':
    'ATTENTION : le scan réel des badges s’ouvrira IMMÉDIATEMENT.\n\nChaque badge scanné sera enregistré pour de bon, même si {eventTypeDef} n’a pas commencé, et le mode entraînement se fermera.\n\nConfirmer ?',
  'pilotage.confirm.phaseDuring':
    'ATTENTION : forcer l’étape sur « Pendant » ouvre IMMÉDIATEMENT le scan réel des badges.\n\nChaque badge scanné sera enregistré pour de bon, quelle que soit la date, et le mode entraînement se fermera.\n\nConfirmer ?',
  'pilotage.confirm.phaseBefore':
    'Forcer l’étape sur « Avant » ?\n\nLa date sera ignorée : le scan réel restera FERMÉ, même le jour J, tant que vous ne remettez pas « Automatique ».',
  'pilotage.confirm.phaseAfter':
    'Forcer l’étape sur « Après » ?\n\nLa date sera ignorée : le scan réel sera FERMÉ tant que vous ne remettez pas « Automatique ».',
  'pilotage.confirm.scanTest':
    'ATTENTION : le scan réel des badges s’ouvrira IMMÉDIATEMENT pour un test.\n\nChaque vrai badge scanné sera enregistré pour de bon. Remettez « Automatique » dès le test fini.\n\nConfirmer ?',
  'pilotage.lock.scanDemo.label': 'Mode entraînement (badges de test)',
  'pilotage.lock.scanDemo.hint':
    'Coché : avant le jour J, les {lexExhibitorPlural} et les animateurs génèrent des badges de test et s’entraînent à la douchette, rien n’est enregistré. Il se ferme de lui-même au début {eventTypeOf}.',
  'pilotage.lock.absent.entrainement': 'Réglage absent sur cette instance : appliquez la migration V86.',
  'pilotage.lock.service.label': 'Application en service (pas de maintenance technique)',
  'pilotage.lock.service.hint':
    'Coché : l’application fonctionne. Décoché : une page de maintenance s’affiche pour tout le monde, {lexExhibitorPlural} compris ; personne ne peut modifier sa fiche et le scan est bloqué. Seuls les administrateurs entrent.',

  // ── Badges de test ───────────────────────────────────────────────────
  'badgesTest.bouton': 'Générer des badges de test (PDF)',
  'badgesTest.bandeau': 'BADGE DE TEST',
  'badgesTest.filigrane': 'TEST',
  'badgesTest.avertissement':
    'Ne fonctionne pas le jour {eventTypeOf}. Ne pas l’utiliser le {eventDateLong}.',
  'badgesTest.piedDePage':
    'Badges de test pour s’entraîner : personnes fictives, rien n’est enregistré. Le jour J, ils sont refusés.',
  'badgesTest.erreur': 'Impossible de générer les badges de test. Réessayez.',
  'badgesTest.carte.titre': 'S’entraîner au scan avant le jour J',
  'badgesTest.carte.texte':
    'Imprimez des badges de test (ou affichez-les sur un autre téléphone) et scannez-les avec la douchette : vous découvrez l’outil sans rien enregistrer. Ils ne fonctionnent pas le jour J.',
  'badgesTest.carte.scanner': 'Ouvrir la douchette',
  'badgesTest.scanJourJ': 'Ceci est un badge de test, il ne compte pas. Rien n’a été enregistré.',

  // ── Douchette : l'état, toujours affiché ─────────────────────────────
  'scanEtat.entrainement': 'Mode entraînement : seuls les badges de test fonctionnent, rien n’est enregistré.',
  'scanEtat.reel': 'Scan réel ouvert : chaque badge scanné est enregistré.',
  'scanEtat.badgesEcran': 'Badges de test à l’écran',
};
